import React, { useEffect, useState } from "react";
import { matchesMember } from "../lib/memberSearch";
import { SignalPulse } from "../components/SignalPulse";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Flame, ListOrdered, ArrowUp, ArrowDown, ChevronsUp, Check, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "../components/Media";
import { CategoryPill } from "./Feed";
import { RealmFilter } from "../components/RealmFilter";
import { FavoriteButton } from "../components/FavoriteButton";
import { useAuth } from "../context/AuthContext";

export default function Directory() {
  const [members, setMembers] = useState(null);
  const [council, setCouncil] = useState([]);
  const [sel, setSel] = useState({ params: {}, realm: "", group: "" });
  const [search, setSearch] = useState("");
  const [beaconOnly, setBeaconOnly] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const shown = (members || []).filter((m) => matchesMember(m, search) && (!beaconOnly || m.beacon_handle));

  const load = async () => {
    const { data } = await api.get("/members", { params: { ...sel.params } });
    setMembers(data);
  };

  useEffect(() => { api.get("/council").then(({ data }) => setCouncil(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [sel]);

  // --- Admin-only manual ordering of the directory ---
  const isAdmin = user?.role === "admin";
  const [arrangeMode, setArrangeMode] = useState(false);
  const [order, setOrder] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const enterArrange = async () => {
    const { data } = await api.get("/members");
    setOrder(data); setArrangeMode(true);
  };
  const moveItem = (i, dir) => setOrder((arr) => {
    const j = i + dir; if (j < 0 || j >= arr.length) return arr;
    const next = arr.slice(); [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const moveTop = (i) => setOrder((arr) => {
    if (i <= 0) return arr; const next = arr.slice(); const [it] = next.splice(i, 1); next.unshift(it); return next;
  });
  const saveOrder = async () => {
    setSavingOrder(true);
    try { await api.post("/admin/directory-order", { ids: order.map((m) => m.id) }); setArrangeMode(false); load(); }
    catch { alert("Could not save the order. Please try again."); }
    finally { setSavingOrder(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">Sovereigns</h1>
          <p className="font-body text-sm text-zinc-500 mt-1">The members forging this network. Connect by realm.</p>
        </div>
        {isAdmin && !arrangeMode && (
          <button onClick={enterArrange} data-testid="directory-arrange-button" className="shrink-0 inline-flex items-center gap-2 border border-[#14A89A]/40 text-[#14A89A] px-3 py-2 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
            <ListOrdered size={14} /> Arrange order
          </button>
        )}
      </header>

      {arrangeMode && (
        <div data-testid="directory-arrange">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <p className="font-body text-xs text-zinc-400 max-w-lg leading-relaxed">
              Use the arrows to set the order. Members you arrange are pinned to the top <span className="text-zinc-300">for everyone</span>, in this exact order. Anyone you don't arrange flows below, newest first, so newly accepted members appear at the top of the un-arranged group. This only changes display order; no profile content is touched.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setArrangeMode(false)} data-testid="arrange-cancel" className="border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-white/5 transition-colors">Cancel</button>
              <button onClick={saveOrder} disabled={savingOrder} data-testid="arrange-save" className="bg-[#14A89A] text-[#0A0A0A] px-4 py-2 font-body text-xs font-medium disabled:opacity-40 flex items-center gap-2">
                {savingOrder ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save order
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {order.map((m, i) => (
              <div key={m.id} data-testid={`arrange-row-${m.id}`} className="flex items-center gap-3 bg-[#121212] border border-white/10 px-3 py-2.5">
                <span className="font-body text-xs text-zinc-600 w-6 text-center shrink-0">{i + 1}</span>
                <Avatar name={m.name} src={m.avatar} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="font-body text-sm text-white truncate">{m.name}</div>
                  <div className="font-body text-[11px] text-zinc-500 truncate">{m.headline}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => moveTop(i)} disabled={i === 0} data-testid={`arrange-top-${m.id}`} title="Move to top" className="p-1.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronsUp size={15} /></button>
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`arrange-up-${m.id}`} title="Move up" className="p-1.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ArrowUp size={15} /></button>
                  <button onClick={() => moveItem(i, 1)} disabled={i === order.length - 1} data-testid={`arrange-down-${m.id}`} title="Move down" className="p-1.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ArrowDown size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!arrangeMode && (
      <>
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or work..." data-testid="directory-search" className="w-full bg-transparent border-b border-white/15 pl-7 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body" />
      </div>

      {council.length > 0 && !search && !sel.realm && (
        <section className="mb-10" data-testid="directory-council">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={15} className="text-[#14A89A]" />
            <h2 className="font-body text-xs tracking-[0.25em] uppercase text-zinc-400">The Council</h2>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
            {council.map((c) => (
              <button key={c.id} onClick={() => navigate(`/profile/${c.id}`)} data-testid={`council-card-${c.id}`} className="shrink-0 w-36 bg-[#121212] border border-[#14A89A]/20 p-4 text-center hover:-translate-y-1 hover:border-[#14A89A]/50 transition-all duration-300 group">
                <div className="flex justify-center relative">
                  <Avatar name={c.name} src={c.avatar} size={56} />
                  {c.is_founder && <span className="absolute -top-1 right-6"><Sparkles size={13} className="text-[#14A89A]" /></span>}
                </div>
                <div className="font-heading text-sm text-white group-hover:text-[#14A89A] transition-colors truncate mt-3">{c.name}</div>
                <div className="font-body text-[10px] tracking-wider uppercase text-[#14A89A] mt-1 leading-tight">{c.council_role_label}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <RealmFilter accent="#14A89A" onChange={setSel} showDesc={false} testIdPrefix="dir-filter" />

      <button onClick={() => setBeaconOnly((v) => !v)} aria-pressed={beaconOnly} data-testid="directory-beacon-filter"
        className={`mb-6 -mt-2 inline-flex items-center gap-2 rounded-full border px-4 py-2 font-body text-xs tracking-[0.15em] uppercase transition-colors ${beaconOnly ? "border-[#14A89A] bg-[#14A89A]/15 text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}>
        <Flame size={14} className="text-[#14A89A]" /> Has a Beacon
        {beaconOnly && members && <span className="tracking-normal normal-case text-zinc-400">({shown.length})</span>}
      </button>

      {members === null ? (
        <div className="flex justify-center py-20"><SignalPulse size={44} /></div>
      ) : shown.length === 0 ? (
        <p className="font-body text-sm text-zinc-500 py-20 text-center" data-testid="directory-empty">{search.trim() ? `No sovereigns match "${search.trim()}".` : beaconOnly ? "No Beacons lit in this realm yet." : "No sovereigns in this realm yet."}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((m) => (
            <div key={m.id} className="relative bg-[#121212] border border-white/10 hover:-translate-y-1 hover:border-white/25 transition-all duration-300 group">
              {m.id !== user?.id && (
                <div className="absolute top-4 right-4 z-20">
                  <FavoriteButton memberId={m.id} initial={m.favorited} size={18} />
                </div>
              )}
              <a
                href={`/profile/${m.id}`}
                onClick={(e) => { e.preventDefault(); navigate(`/profile/${m.id}`); }}
                role="button"
                data-testid="member-card"
                className="block text-left p-6 cursor-pointer no-underline"
              >
                <div className="flex items-start gap-4">
                  <div className="relative shrink-0">
                    <Avatar name={m.name} src={m.avatar} size={52} />
                    {m.beacon_handle && (
                      <span title="Has a Beacon to visit" data-testid="directory-beacon-badge" className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#121212] border border-[#14A89A]/70 flex items-center justify-center">
                        <Flame size={11} className="text-[#14A89A]" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pr-6">
                    <div className="font-heading text-base text-white group-hover:text-[#14A89A] transition-colors"><span className="line-clamp-2 [overflow-wrap:anywhere]">{m.name}</span></div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {(m.domains?.length ? m.domains : [m.contribution_type]).filter(Boolean).map((d) => <CategoryPill key={d} cat={d} />)}
                    </div>
                  </div>
                </div>
                <p className="font-body text-sm text-zinc-300 mt-4 line-clamp-2 ugc-text">{m.headline}</p>
                <p className="font-body text-xs text-zinc-500 mt-3 line-clamp-2 ugc-text">{m.offering}</p>
              </a>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
